import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { formatDubai } from '../../utils/dubaiTime';
import './auditLogs.css';

const PAGE_SIZE = 50;
const IGNORED_KEYS = ['updated_at', 'updated_by'];

function stableStringify(value) {
    if (value === undefined) return '__undefined__';
    return JSON.stringify(value);
}

function buildDiff(oldData, newData, ignoredKeys = []) {
    const o = oldData || {};
    const n = newData || {};
    const keys = Array.from(new Set([...Object.keys(o), ...Object.keys(n)]))
        .filter((k) => !ignoredKeys.includes(k));
    const changes = [];
    for (const k of keys) {
        const before = o[k];
        const after = n[k];
        if (stableStringify(before) !== stableStringify(after)) {
            changes.push({ field: k, before, after });
        }
    }
    changes.sort((a, b) => a.field.localeCompare(b.field));
    return changes;
}

const PRESETS = [
    { key: '', label: 'All' },
    // Bookings/properties are only ever archived (soft flag), never deleted
    { key: 'deleted_bookings', label: 'Archived Bookings' },
    // The commonest question on this page — and it had no button, while
    // properties had one. Human edits only; the automated churn is filtered
    // server-side (see routes/auditLogs.js).
    { key: 'booking_changes', label: 'Booking Changes' },
    { key: 'deleted_properties', label: 'Archived Properties' },
    { key: 'property_changes', label: 'Property Changes' },
    { key: 'deleted_addons', label: 'Deleted Add-ons' },
    { key: 'client_changes', label: 'Client Changes' },
    { key: 'stock', label: 'Warehouse Stock' },
    { key: 'team_changes', label: 'Team Changes' },
    { key: 'attendance', label: 'Attendance' },
    { key: 'today', label: 'Today' },
    { key: 'last_24h', label: 'Last 24h' },
    { key: 'last_7d', label: 'Last 7 Days' },
];

const TABLE_OPTIONS = [
    { value: '', label: 'All tables' },
    { value: 'bookings', label: 'Bookings' },
    { value: 'booking_addons', label: 'Booking Add-ons' },
    { value: 'booking_cleaners', label: 'Booking Cleaners' },
    { value: 'booking_tasks', label: 'Booking Tasks' },
    { value: 'booking_reports', label: 'Booking Reports' },
    { value: 'properties', label: 'Properties' },
    { value: 'property_addresses', label: 'Property Addresses' },
    { value: 'property_sq', label: 'Property Quantities' },
    { value: 'property_tasks', label: 'Property Tasks' },
    { value: 'client_tasks', label: 'Client Tasks' },
    { value: 'laundry_images', label: 'Laundry Images' },
    { value: 'client_requests', label: 'Client Requests' },
];

const ACTION_OPTIONS = [
    { value: '', label: 'All actions' },
    { value: 'INSERT', label: 'Insert' },
    { value: 'UPDATE', label: 'Update' },
    { value: 'DELETE', label: 'Delete' },
    { value: 'REQUEST', label: 'Request (client)' },
    { value: 'APPROVE', label: 'Approve' },
    { value: 'REJECT', label: 'Reject' },
    { value: 'WITHDRAW', label: 'Withdraw (client)' },
];

export default function AuditLogs() {
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    // Past 10,000 the API stops counting and says so — see routes/auditLogs.js.
    const [totalCapped, setTotalCapped] = useState(false);
    const [offset, setOffset] = useState(0);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    /* The preset pills defer to Apply too (2026-09-02). They ARE filters -- one
       click setting a whole intent -- and having them fire while the fields beside
       them waited made the bar behave two different ways at once. */
    const [draftPreset, setDraftPreset] = useState('');
    const [appliedPreset, setAppliedPreset] = useState('');
    const [clients, setClients] = useState([]);
    const [users, setUsers] = useState([]);

    /* TWO COPIES, ON PURPOSE (2026-09-01) — same split as the leads filter bar.
       `draft` is what the controls show; `applied` is what the query is built
       from. Typing a booking id used to fire one request per digit, and each
       one landed on a different page of results. */
    const EMPTY_FILTERS = {
        client_id: '',
        booking_id: '',
        property_id: '',
        changed_by: '',
        search: '',
        table_name: '',
        action: '',
        date_from: '',
        date_to: '',
    };
    const [draft, setDraft] = useState(EMPTY_FILTERS);
    const [applied, setApplied] = useState(EMPTY_FILTERS);

    const [showAdvanced, setShowAdvanced] = useState(false);

    const [selectedId, setSelectedId] = useState(null);
    const [selectedDetails, setSelectedDetails] = useState(null);
    const [detailsLoading, setDetailsLoading] = useState(false);

    const page = Math.floor(offset / PAGE_SIZE) + 1;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    // Load clients for the dropdown
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get('/api/clients/active');
                setClients(res.data);
            } catch (e) {
                console.error('Failed to load clients', e);
            }
        })();
    }, []);

    // Load users for the dropdown
    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get('/api/users');
                setUsers(Array.isArray(res.data) ? res.data : (res.data?.users || []));
            } catch (e) {
                console.error('Failed to load users', e);
            }
        })();
    }, []);

    const queryString = useMemo(() => {
        const p = new URLSearchParams();
        p.set('limit', String(PAGE_SIZE));
        p.set('offset', String(offset));
        if (appliedPreset) p.set('preset', appliedPreset);
        Object.entries(applied).forEach(([k, v]) => {
            if (v !== '' && v !== null && v !== undefined) p.set(k, v);
        });
        return p.toString();
    }, [appliedPreset, applied, offset]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.get(`/api/audit-logs?${queryString}`);
            setRows(res.data.rows || []);
            setTotal(Number(res.data.total || 0));
            setTotalCapped(!!res.data.total_is_capped);
        } catch (e) {
            console.error(e);
            setError('Failed to load audit logs.');
        } finally {
            setLoading(false);
        }
    }, [queryString]);

    const fetchDetails = useCallback(async (id) => {
        setDetailsLoading(true);
        setSelectedDetails(null);
        try {
            const res = await axios.get(`/api/audit-logs/${id}`);
            setSelectedDetails(res.data);
        } catch (e) {
            console.error(e);
            setSelectedDetails(null);
        } finally {
            setDetailsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    useEffect(() => {
        if (!selectedId) return;
        fetchDetails(selectedId);
    }, [selectedId, fetchDetails]);

    function updateFilter(key, value) {
        setDraft((prev) => ({ ...prev, [key]: value }));
    }

    /* Apply is the only thing that moves the table. Paging back to the first row
       and dropping the open detail belong here rather than in updateFilter --
       the row you were reading should survive you typing a date you have not
       committed to yet. */
    function applyFilters() {
        setOffset(0);
        setSelectedId(null);
        setSelectedDetails(null);
        setApplied(draft);
        setAppliedPreset(draftPreset);
    }

    const dirty = JSON.stringify(draft) !== JSON.stringify(applied)
        || draftPreset !== appliedPreset;
    const onFilterKey = (e) => { if (e.key === 'Enter') applyFilters(); };

    function applyPreset(p) {
        setDraftPreset(p);
    }

    function clearAll() {
        setDraftPreset('');
        setAppliedPreset('');
        setOffset(0);
        setSelectedId(null);
        setSelectedDetails(null);
        setDraft(EMPTY_FILTERS);
        setApplied(EMPTY_FILTERS);
    }

    // What is ON the table, not what is half-typed into the bar.
    const hasActiveFilters = appliedPreset || Object.values(applied).some(v => v !== '');
    /* Clear is always rendered now rather than appearing when a filter lands —
       a button materialising next to Apply shifted it under the cursor. It takes
       the DRAFT into account too, so it can undo something typed but not yet
       applied. */
    const anyFilters = hasActiveFilters || draftPreset
        || Object.values(draft).some(v => v !== '');

    const computed = useMemo(() => {
        if (!selectedDetails) {
            return { changes: [], hasChanges: false };
        }
        const actionUpper = String(selectedDetails.action || '').toUpperCase();
        const ignored = actionUpper === 'UPDATE' ? IGNORED_KEYS : [];
        const changes = buildDiff(selectedDetails.old_data || {}, selectedDetails.new_data || {}, ignored);
        return { changes, hasChanges: changes.length > 0 };
    }, [selectedDetails]);

    const clientOptions = useMemo(
        () => clients.map(c => ({ value: c.id, label: c.display_name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || `Client #${c.id}` })),
        [clients]
    );

    const userOptions = useMemo(
        () => users.map(u => ({ value: u.id, label: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email || `User #${u.id}` })),
        [users]
    );

    const selectedClient = clientOptions.find(o => String(o.value) === String(draft.client_id)) || null;
    const selectedUser = userOptions.find(o => String(o.value) === String(draft.changed_by)) || null;

    const tableSelected = TABLE_OPTIONS.find(o => o.value === draft.table_name) || TABLE_OPTIONS[0];
    const actionSelected = ACTION_OPTIONS.find(o => o.value === draft.action) || ACTION_OPTIONS[0];

    const renderValue = (v) => {
        if (v === null || v === undefined) return <span className="al2-muted">—</span>;
        if (typeof v === 'boolean') return v ? 'Yes' : 'No';
        if (typeof v === 'object') return <code>{JSON.stringify(v)}</code>;
        const s = String(v);
        return s.length > 80 ? s.slice(0, 80) + '…' : s;
    };

    return (
        <div className="al2">
            <div className="al2__header">
                <div>
                    <h2 className="al2__title">Audit Logs</h2>
                    <div className="al2__subtitle">
                        Track every change. Filter by client, booking, property, user, or quick preset.
                    </div>
                </div>

            </div>

            {/* Preset pills */}
            <div className="al2__presets">
                {PRESETS.map(p => (
                    <button
                        key={p.key}
                        className={`al2__preset ${draftPreset === p.key ? 'is-active' : ''}`}
                        onClick={() => applyPreset(p.key)}
                    >
                        {p.label}
                    </button>
                ))}
            </div>

            {/* Main filters */}
            <div className="al2__filters">
                <div className="al2__filter al2__filter--wide">
                    <label>Client</label>
                    <Select
                        classNamePrefix="al2sel"
                        options={clientOptions}
                        value={selectedClient}
                        onChange={(opt) => updateFilter('client_id', opt ? opt.value : '')}
                        placeholder="All clients"
                        isClearable
                    />
                </div>
                <div className="al2__filter">
                    <label>Booking ID</label>
                    <input
                        type="number"
                        value={draft.booking_id}
                        onChange={(e) => updateFilter('booking_id', e.target.value)}
                        onKeyDown={onFilterKey}
                        placeholder="e.g. 15821"
                    />
                </div>
                <div className="al2__filter">
                    <label>Property ID</label>
                    <input
                        type="number"
                        value={draft.property_id}
                        onChange={(e) => updateFilter('property_id', e.target.value)}
                        onKeyDown={onFilterKey}
                        placeholder="e.g. 45"
                    />
                </div>
                <div className="al2__filter al2__filter--wide">
                    <label>Changed By</label>
                    <Select
                        classNamePrefix="al2sel"
                        options={userOptions}
                        value={selectedUser}
                        onChange={(opt) => updateFilter('changed_by', opt ? opt.value : '')}
                        placeholder="Anyone"
                        isClearable
                    />
                </div>
                <div className="al2__filter">
                    <label>From</label>
                    <input
                        type="date"
                        value={draft.date_from}
                        onChange={(e) => updateFilter('date_from', e.target.value)}
                        onKeyDown={onFilterKey}
                    />
                </div>
                <div className="al2__filter">
                    <label>To</label>
                    <input
                        type="date"
                        value={draft.date_to}
                        onChange={(e) => updateFilter('date_to', e.target.value)}
                        onKeyDown={onFilterKey}
                    />
                </div>
                {/* WITH THE FILTERS, NOT IN THE PAGE HEADER (2026-09-02). These act
                    on this panel, so they belong beside the last field in it —
                    sitting up by the title they read as page-level actions and you
                    had to look away from what you were editing to commit it.

                    Clear first, then Apply: the destructive one never sits where a
                    hand travelling to the primary button lands. Apply stays dead
                    until the panel and the table actually disagree, so it doubles
                    as the answer to "did that take effect?". */}
                <div className="al2__filter al2__filterActions">
                    <div className="al2__filterActionsRow">
                        <button className="al2__btn al2__btn--ghost" onClick={clearAll}
                            disabled={!anyFilters}>
                            Clear Filters
                        </button>
                        <button className={`al2__btn al2__btn--apply ${dirty ? 'is-dirty' : 'al2__btn--ghost'}`}
                            onClick={applyFilters} disabled={!dirty}
                            title={dirty ? 'Apply these filters' : 'Filters are up to date'}>
                            Apply{dirty ? ' •' : ''}
                        </button>
                    </div>
                </div>
            </div>

            <div className="al2__advancedToggle">
                <button className="al2__linkBtn" onClick={() => setShowAdvanced(!showAdvanced)}>
                    {showAdvanced ? 'Hide' : 'Show'} advanced filters
                </button>
            </div>

            {showAdvanced && (
                <div className="al2__filters">
                    <div className="al2__filter al2__filter--wide">
                        <label>Table</label>
                        <Select
                            classNamePrefix="al2sel"
                            options={TABLE_OPTIONS}
                            value={tableSelected}
                            onChange={(opt) => updateFilter('table_name', opt ? opt.value : '')}
                            isSearchable={false}
                        />
                    </div>
                    <div className="al2__filter">
                        <label>Action</label>
                        <Select
                            classNamePrefix="al2sel"
                            options={ACTION_OPTIONS}
                            value={actionSelected}
                            onChange={(opt) => updateFilter('action', opt ? opt.value : '')}
                            isSearchable={false}
                        />
                    </div>
                </div>
            )}

            {error && <div className="al2__error">{error}</div>}

            <div className="al2__layout">
                {/* List */}
                <div className="al2__list">
                    <div className="al2__listHeader">
                        <div className="al2__count">
                            {loading
                                ? 'Loading…'
                                : `${total.toLocaleString()}${totalCapped ? '+' : ''} results`}
                        </div>
                        <div className="al2__pagination">
                            <button
                                className="al2__btn al2__btn--ghost al2__btn--sm"
                                disabled={offset === 0 || loading}
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                            >
                                ←
                            </button>
                            <div className="al2__pageinfo">
                                {page} / {totalPages}
                            </div>
                            <button
                                className="al2__btn al2__btn--ghost al2__btn--sm"
                                disabled={(!totalCapped && offset + PAGE_SIZE >= total) || loading}
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                            >
                                →
                            </button>
                        </div>
                    </div>

                    {rows.length === 0 && !loading ? (
                        <div className="al2__empty">No audit logs found matching your filters.</div>
                    ) : (
                        <div className="al2__rows">
                            {rows.map((r) => {
                                // Asia/Dubai render (item 8) — zoneless fromISO used the device TZ.
                                const dt = formatDubai(r.changed_at, 'dd MMM yy, HH:mm');
                                const isSelected = selectedId === r.id;
                                const actionKey = String(r.action || '').toLowerCase();

                                return (
                                    <div
                                        key={r.id}
                                        className={`al2__row ${isSelected ? 'is-selected' : ''}`}
                                        onClick={() => setSelectedId(r.id)}
                                        role="button"
                                        tabIndex={0}
                                    >
                                        <div className="al2__rowMain">
                                            <div className="al2__rowTop">
                                                <span className={`al2__actionPill al2__actionPill--${actionKey}`}>
                                                    {r.action}
                                                </span>
                                                <span className="al2__rowSummary">{r.summary}</span>
                                            </div>
                                            {r.context && (
                                                <div className="al2__rowContext">{r.context}</div>
                                            )}
                                            <div className="al2__rowFoot">
                                                <span className="al2__rowDate">{dt}</span>
                                                <span className="al2__rowSep">•</span>
                                                <span>{r.changed_by_name || 'System'}</span>
                                                {r.changed_by_role === 'client' && (
                                                    <span className="al2__clientTag">Client</span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Details */}
                <div className="al2__details">
                    {!selectedId ? (
                        <div className="al2__empty al2__empty--large">
                            <div className="al2__emptyIcon">🔍</div>
                            <div>Select an entry to view what changed</div>
                        </div>
                    ) : detailsLoading ? (
                        <div className="al2__empty">Loading details…</div>
                    ) : !selectedDetails ? (
                        <div className="al2__empty">No details found.</div>
                    ) : (
                        <div className="al2__detailsCard">
                            <div className="al2__detailsHeader">
                                <div>
                                    <div className="al2__detailsTitle">
                                        {selectedDetails.table_name === 'bookings' && selectedDetails.booking_id ? (
                                            <Link to={`/bookings/${selectedDetails.booking_id}`} className="al2__recordLink">
                                                {selectedDetails.table_name} #{selectedDetails.record_id}
                                            </Link>
                                        ) : selectedDetails.table_name === 'properties' && selectedDetails.property_id ? (
                                            <Link to={`/properties/${selectedDetails.property_id}`} className="al2__recordLink">
                                                {selectedDetails.table_name} #{selectedDetails.record_id}
                                            </Link>
                                        ) : (
                                            <>{selectedDetails.table_name} #{selectedDetails.record_id}</>
                                        )}
                                    </div>
                                    <div className="al2__detailsMeta">
                                        <span className={`al2__actionPill al2__actionPill--${String(selectedDetails.action).toLowerCase()}`}>
                                            {selectedDetails.action}
                                        </span>
                                        <span>{formatDubai(selectedDetails.changed_at, 'dd MMM yyyy, HH:mm')}</span>
                                        <span>
                                            by {selectedDetails.changed_by_name || 'System'}
                                            {selectedDetails.changed_by_role === 'client' && (
                                                <span className="al2__clientTag">Client</span>
                                            )}
                                        </span>
                                    </div>
                                    {(selectedDetails.client_name || selectedDetails.property_label) && (
                                        <div className="al2__detailsContext">
                                            {selectedDetails.client_name && (
                                                <span>
                                                    <strong>Client:</strong>{' '}
                                                    {selectedDetails.client_id ? (
                                                        <Link to={`/clients/${selectedDetails.client_id}`} className="al2__inlineLink">
                                                            {selectedDetails.client_name}
                                                        </Link>
                                                    ) : selectedDetails.client_name}
                                                </span>
                                            )}
                                            {selectedDetails.property_label && (
                                                <span>
                                                    <strong>Property:</strong>{' '}
                                                    {selectedDetails.property_id ? (
                                                        <Link to={`/properties/${selectedDetails.property_id}`} className="al2__inlineLink">
                                                            {selectedDetails.property_label}
                                                        </Link>
                                                    ) : selectedDetails.property_label}
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="al2__changes">
                                <div className="al2__sectionTitle">What changed</div>
                                {!computed.hasChanges ? (
                                    <div className="al2__muted">No field changes recorded.</div>
                                ) : (
                                    <div className="al2__changesList">
                                        {computed.changes.map((c) => (
                                            <div key={c.field} className="al2__change">
                                                <div className="al2__changeField">{c.field}</div>
                                                <div className="al2__changeValues">
                                                    <div className="al2__changeBefore">
                                                        <div className="al2__changeLabel">Before</div>
                                                        <div className="al2__changeValue">{renderValue(c.before)}</div>
                                                    </div>
                                                    <div className="al2__changeArrow">→</div>
                                                    <div className="al2__changeAfter">
                                                        <div className="al2__changeLabel">After</div>
                                                        <div className="al2__changeValue">{renderValue(c.after)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
